class KedsProduct extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.groups = [...this.querySelectorAll('[data-option-index]')];
    this.variantSelect = this.querySelector('[data-variant-id]');
    this.track = this.querySelector('[data-media-track]');
    this.dialog = this.querySelector('[data-image-dialog]');
    this.querySelector('[data-option-groups]').hidden = false;
    this.querySelector('[data-variant-fallback]').hidden = true;
    this.querySelector('[data-gallery-controls]')?.removeAttribute('hidden');

    // Gallery Show More toggle
    const moreBtn = this.querySelector('[data-show-more-toggle]');
    if (moreBtn && this.track) {
      moreBtn.addEventListener('click', () => {
        const isExpanded = this.track.classList.toggle('is-expanded');
        moreBtn.setAttribute('aria-expanded', String(isExpanded));
        const textSpan = moreBtn.querySelector('[data-show-more-text]');
        if (textSpan) {
          textSpan.textContent = isExpanded ? (moreBtn.dataset.textLess || '↑ Show Less') : (moreBtn.dataset.textMore || '↓ Show More');
        }
      }, { signal });
    }

    // Rating breakdown popover toggle and outside click
    const ratingTrigger = this.querySelector('[data-rating-trigger]');
    const ratingPopover = this.querySelector('[data-rating-popover]');
    if (ratingTrigger && ratingPopover) {
      ratingTrigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const isHidden = ratingPopover.hasAttribute('hidden');
        if (isHidden) {
          ratingPopover.removeAttribute('hidden');
          ratingTrigger.setAttribute('aria-expanded', 'true');
        } else {
          ratingPopover.setAttribute('hidden', '');
          ratingTrigger.setAttribute('aria-expanded', 'false');
        }
      }, { signal });

      document.addEventListener('click', (event) => {
        if (!ratingPopover.contains(event.target) && !ratingTrigger.contains(event.target)) {
          ratingPopover.setAttribute('hidden', '');
          ratingTrigger.setAttribute('aria-expanded', 'false');
        }
      }, { signal });
    }

    // Jump to Customer Reviews and open accordion
    const jumpReviews = this.querySelector('[data-jump-reviews]');
    if (jumpReviews) {
      jumpReviews.addEventListener('click', (event) => {
        if (ratingPopover) {
          ratingPopover.setAttribute('hidden', '');
          ratingTrigger?.setAttribute('aria-expanded', 'false');
        }
        const href = jumpReviews.getAttribute('href');
        const targetId = href ? href.replace('#', '') : '';
        const targetEl = targetId ? document.getElementById(targetId) : null;
        if (targetEl) {
          const details = targetEl.matches('details') ? targetEl : targetEl.querySelector('details');
          if (details) details.open = true;
          targetEl.scrollIntoView({ behavior: 'smooth' });
        }
      }, { signal });
    }

    this.addEventListener('change', event => {
      if (event.target.matches('input[type=radio]')) this.selectVariant();
    }, { signal });
    this.addEventListener('click', event => {
      const step = event.target.closest('[data-gallery-step]');
      if (step) this.track.scrollBy({ left: Number(step.dataset.galleryStep) * this.track.clientWidth, behavior: this.motion() });
      const imageLink = event.target.closest('[data-image-zoom]');
      if (imageLink && this.dialog.showModal) {
        event.preventDefault();
        this.zoomOpener = imageLink;
        const image = this.querySelector('[data-zoom-image]');
        image.src = imageLink.href;
        image.alt = imageLink.querySelector('img').alt;
        this.dialog.showModal();
      }
      if (event.target.closest('[data-zoom-close]') || event.target === this.dialog) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => this.zoomOpener?.focus(), { signal });
    this.track.addEventListener('scroll', () => {
      const counter = this.querySelector('[data-gallery-index]');
      if (counter) counter.textContent = Math.round(this.track.scrollLeft / this.track.clientWidth) + 1;
    }, { signal, passive: true });
    window.addEventListener('popstate', () => {
      const id = new URL(location.href).searchParams.get('variant');
      const variant = this.variants.find(value => String(value.id) === id) || this.variants[0];
      if (!variant) return;
      this.groups.forEach((group, index) => {
        group.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.options[index]; });
      });
      this.selectVariant(false);
    }, { signal });
    this.selectVariant(false, false);
  }
  disconnectedCallback() { this.abort?.abort(); }
  motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
  selectVariant(updateUrl = true, updateMedia = true) {
    const selected = this.groups.map(group => group.querySelector('input:checked')?.value);
    const variant = this.groups.length ? this.variants.find(value => value.options.every((option, index) => option === selected[index])) : this.variants[0];
    this.groups.forEach((group, index) => {
      const label = group.querySelector('[data-selected-label]');
      if (label) label.textContent = selected[index] || '';
      group.querySelectorAll('input').forEach(input => {
        const candidates = this.variants.filter(value => value.options[index] === input.value && selected.slice(0, index).every((option, prior) => option === value.options[prior]));
        input.disabled = candidates.length === 0;
        input.closest('label').classList.toggle('is-sold-out', candidates.length > 0 && candidates.every(value => !value.available));
      });
    });
    const add = this.querySelector('[data-add]');
    this.variantSelect.disabled = !variant;
    add.disabled = !variant?.available;
    add.textContent = !variant ? 'Unavailable' : variant.available ? 'Add to Bag' : 'Sold out';
    this.querySelector('[data-variant-status]').textContent = !variant ? 'This combination is unavailable. Choose another option.' : '';
    if (variant) {
      this.variantSelect.value = String(variant.id);
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      this.querySelector('[data-price]').textContent = variant.price;
      const compare = this.querySelector('[data-compare-price]');
      compare.hidden = !variant.comparePrice;
      compare.textContent = variant.comparePrice || '';
      if (updateMedia && variant.mediaId) {
        const media = this.querySelector(`[data-media-id="${variant.mediaId}"]`);
        if (media) {
          this.track.prepend(media);
          this.track.scrollTo({ left: 0, behavior: 'auto' });
          this.track.querySelectorAll('video').forEach(video => video.pause());
        }
      }
    }
    if (updateUrl) {
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
    }
  }
}
if (!customElements.get('keds-product')) customElements.define('keds-product', KedsProduct);

class KedsRecommendations extends HTMLElement {
  async connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const response = await fetch(this.dataset.url, { signal: this.abort.signal });
      if (!response.ok) return;
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = document.querySelector('[data-recommendation-content]');
      if (content?.querySelector('.product-card')) { this.innerHTML = content.innerHTML; this.hidden = false; }
    } catch (error) { if (error.name !== 'AbortError') this.hidden = true; }
  }
  disconnectedCallback() { this.abort?.abort(); }
}
if (!customElements.get('keds-recommendations')) customElements.define('keds-recommendations', KedsRecommendations);

document.addEventListener('click', (event) => {
  const backToTopBtn = event.target.closest('[data-back-to-top], .pdp-back-to-top');
  if (backToTopBtn) {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Jump to reviews when clicking rating score or stars link
  const ratingScoreLink = event.target.closest('.pdp-rating-score-link, .pdp-rating-stars--blue');
  if (ratingScoreLink) {
    event.preventDefault();
    const reviewsAcc = document.querySelector('[data-reviews-accordion], #CustomerReviews');
    if (reviewsAcc) {
      if (reviewsAcc.matches('details')) reviewsAcc.open = true;
      else {
        const details = reviewsAcc.querySelector('details');
        if (details) details.open = true;
      }
      reviewsAcc.scrollIntoView({ behavior: 'smooth' });
    }
  }
});

// Interactive Reviews Controller
function initReviewsInteractive(root = document) {
  const reviewsApp = root.querySelector('[data-reviews-app]');
  if (!reviewsApp || reviewsApp.__initialized) return;
  reviewsApp.__initialized = true;

  const reviewsList = reviewsApp.querySelector('[data-reviews-list]');
  const filterPills = reviewsApp.querySelectorAll('[data-review-filter]');
  const sortSelect = reviewsApp.querySelector('[data-review-sort]');
  const writeToggle = reviewsApp.querySelector('[data-write-review-toggle]');
  const reviewForm = reviewsApp.querySelector('[data-review-form]');
  const cancelBtn = reviewsApp.querySelector('[data-cancel-review]');
  const starBtns = reviewsApp.querySelectorAll('[data-star-val]');
  const ratingInput = reviewsApp.querySelector('[data-rating-input]');
  const successMsg = reviewsApp.querySelector('[data-form-success]');

  // 1. Filter reviews by rating
  filterPills.forEach(pill => {
    pill.addEventListener('click', () => {
      filterPills.forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      const targetRating = pill.dataset.reviewFilter;
      const cards = reviewsList.querySelectorAll('.pdp-review-card');
      cards.forEach(card => {
        if (targetRating === 'all' || card.dataset.rating === targetRating) {
          card.style.display = '';
        } else {
          card.style.display = 'none';
        }
      });
    });
  });

  // 2. Sort reviews
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const criteria = sortSelect.value;
      const cards = Array.from(reviewsList.querySelectorAll('.pdp-review-card'));
      cards.sort((a, b) => {
        if (criteria === 'newest') return new Date(b.dataset.date || 0) - new Date(a.dataset.date || 0);
        if (criteria === 'highest') return Number(b.dataset.rating || 0) - Number(a.dataset.rating || 0);
        if (criteria === 'lowest') return Number(a.dataset.rating || 0) - Number(b.dataset.rating || 0);
        if (criteria === 'helpful') return Number(b.dataset.helpful || 0) - Number(a.dataset.helpful || 0);
        return 0;
      });
      cards.forEach(card => reviewsList.appendChild(card));
    });
  }

  // 3. Helpful voting
  reviewsList.addEventListener('click', event => {
    const helpfulBtn = event.target.closest('[data-helpful-btn]');
    if (helpfulBtn && !helpfulBtn.classList.contains('is-voted')) {
      helpfulBtn.classList.add('is-voted');
      const countEl = helpfulBtn.querySelector('[data-helpful-count]');
      if (countEl) {
        const cur = Number(countEl.textContent || 0) + 1;
        countEl.textContent = cur;
        const card = helpfulBtn.closest('.pdp-review-card');
        if (card) card.dataset.helpful = String(cur);
      }
    }
  });

  // 4. Write a Review Toggle
  if (writeToggle && reviewForm) {
    writeToggle.addEventListener('click', () => {
      reviewForm.hidden = !reviewForm.hidden;
      if (!reviewForm.hidden) reviewForm.scrollIntoView({ behavior: 'smooth' });
    });
  }
  if (cancelBtn && reviewForm) {
    cancelBtn.addEventListener('click', () => {
      reviewForm.hidden = true;
    });
  }

  // 5. Star Selection in Form
  starBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const val = Number(btn.dataset.starVal);
      if (ratingInput) ratingInput.value = val;
      starBtns.forEach(b => {
        b.classList.toggle('is-selected', Number(b.dataset.starVal) <= val);
      });
    });
  });

  // 6. Review Form Submission
  if (reviewForm) {
    reviewForm.addEventListener('submit', event => {
      event.preventDefault();
      const formData = new FormData(reviewForm);
      const rating = formData.get('rating') || '5';
      const title = formData.get('title') || 'Great product';
      const body = formData.get('body') || '';
      const author = formData.get('author') || 'Verified Shopper';

      const starsHtml = '★'.repeat(Number(rating)) + '☆'.repeat(5 - Number(rating));
      const newCard = document.createElement('article');
      newCard.className = 'pdp-review-card';
      newCard.dataset.rating = String(rating);
      newCard.dataset.date = new Date().toISOString().split('T')[0];
      newCard.dataset.helpful = '0';
      newCard.innerHTML = `
        <div class="pdp-review-card__header">
          <div class="pdp-review-card__author-info">
            <span class="pdp-review-card__author">${escapeHtml(author)}</span>
            <span class="pdp-review-card__badge">✔ Verified Buyer</span>
          </div>
          <time class="pdp-review-card__date">Just now</time>
        </div>
        <div class="pdp-review-card__stars">
          <span class="pdp-star pdp-star--blue">${starsHtml}</span>
        </div>
        <h5 class="pdp-review-card__title">${escapeHtml(title)}</h5>
        <p class="pdp-review-card__body">${escapeHtml(body)}</p>
        <div class="pdp-review-card__meta">
          <span><strong>Fit:</strong> True to size</span>
        </div>
        <div class="pdp-review-card__footer">
          <button type="button" class="pdp-review-card__helpful" data-helpful-btn>
            Helpful? 👍 Yes (<span data-helpful-count>0</span>)
          </button>
        </div>
      `;

      reviewsList.prepend(newCard);
      if (successMsg) {
        successMsg.hidden = false;
        setTimeout(() => { successMsg.hidden = true; reviewForm.hidden = true; }, 2500);
      }
      reviewForm.reset();
    });
  }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

document.addEventListener('DOMContentLoaded', () => initReviewsInteractive());
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initReviewsInteractive();
}
